import { Link } from 'react-router-dom';

import { capitalize, getCreditsForTier } from '../../config/pricingConfig';
import {
  Entitlement,
  SubscriptionStatus,
} from '../../hooks/useSubscriptionStatus';
import { StatusUnavailable } from '../StatusUnavailable';
import { trackButtonClick } from '../../services/telemetry';

interface CreditSummaryProps {
  subscriptionStatus: SubscriptionStatus | null;
  entitlement: Entitlement;
  onRetry?: () => void;
}

/**
 * Workspace credits panel: shows the user's monthly + extra credit balances,
 * explains how credits are consumed, and offers a "Get credit" CTA to pricing.
 * Sits alongside the upload box so members know, before uploading, whether a
 * generation will use a credit or run as a free demo.
 */
export const CreditSummary = ({
  subscriptionStatus,
  entitlement,
  onRetry,
}: CreditSummaryProps) => {
  // A failed read used to be indistinguishable from a slow one, so the panel said
  // "Loading credits…" forever — the user was never told their balance was
  // unknown, and had no way to ask for it again short of reloading the page.
  if (entitlement === 'failed') {
    return (
      <StatusUnavailable
        message="We couldn't load your credit balance."
        onRetry={onRetry}
        className="h-full min-h-[180px]"
      />
    );
  }

  if (!subscriptionStatus) {
    return (
      <div
        className={`
          flex h-full min-h-[180px] items-center justify-center rounded-2xl
          border border-gray-200 bg-white p-6 text-sm text-gray-400
        `}
      >
        Loading credits…
      </div>
    );
  }

  const monthlyCredits = subscriptionStatus.creditBalance ?? 0;
  const extraCredits = subscriptionStatus.extraCredits ?? 0;
  // Keep in sync with the backend's "subscribed" definition
  // (active / trialing / past_due) so trialing users see their monthly credits
  // and the correct copy rather than the "no subscription" state.
  const isSubscribed =
    subscriptionStatus.status === 'active' ||
    subscriptionStatus.status === 'trialing' ||
    subscriptionStatus.status === 'past_due';
  const maxMonthlyCredits = getCreditsForTier(
    subscriptionStatus.subscriptionTier ?? 'free'
  );
  const hasCredits = monthlyCredits + extraCredits > 0;
  const planLabel = capitalize(subscriptionStatus.subscriptionTier ?? 'free');

  return (
    <div
      className={`
        flex h-full flex-col justify-center rounded-2xl border border-gray-200
        bg-white p-6
      `}
    >
      {/* Current plan + "Get credit" CTA, shown to every member. */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-base font-semibold text-gray-800">
          Current Plan: {planLabel}
        </p>
        <Link
          to="/pricing"
          onClick={() => trackButtonClick('Workspace', 'Get Credit')}
          className={`
            inline-flex shrink-0 items-center rounded-full border
            border-gray-800 bg-transparent px-4 py-1.5 text-sm font-semibold
            text-gray-800 transition-colors duration-75
            hover:border-yellow-500 hover:bg-yellow-400 hover:text-gray-800
          `}
        >
          Get credit
        </Link>
      </div>

      {/* Balances are always shown (0 included); the plan line above gives them
          context, so a free user reading "Monthly Credit: 0/0" makes sense. */}
      <div className="space-y-1 text-sm text-gray-700">
        <p className="flex items-center justify-between">
          <span>Monthly Credit:</span>
          <span className="font-semibold text-gray-800">
            {monthlyCredits} / {maxMonthlyCredits}
          </span>
        </p>
        <p className="flex items-center justify-between">
          <span>Extra Credit:</span>
          <span className="font-semibold text-gray-800">{extraCredits}</span>
        </p>
      </div>

      <div className="mt-4 border-t border-gray-100 pt-4">
        {hasCredits ? (
          <div className="space-y-2 text-sm text-gray-600">
            <p>Each successful generation uses 1 credit.</p>
            {isSubscribed ? (
              <p>
                Monthly credits will be used first and reset at your next
                billing cycle. Extra credits never expire.
              </p>
            ) : extraCredits > 0 ? (
              <p>Extra credits never expire.</p>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-gray-600">
            Upload for free, up to 3 systems 1 page (image, pdf) or 18 measures
            (MusicXML) per project.{' '}
            <Link
              to="/pricing"
              className="text-orange-600 underline hover:text-orange-700"
            >
              Subscribe or buy a credit pack
            </Link>{' '}
            for full-length generation.
          </p>
        )}
      </div>
    </div>
  );
};
